/**
 * Разбор адреса вкладки в экран. Чистая функция: не трогает DOM и историю
 * браузера, поэтому проверяется без окружения (FR-ADM-002).
 */

export type ScreenName =
  | 'instances'
  | 'chats'
  | 'webhooks'
  | 'contacts'
  | 'access'
  | 'pipelines'
  | 'credentials'
  | 'variables'

export interface Route {
  screen: ScreenName
  id?: string
}

/*
 * Названия разделов — теми словами, которыми о них говорит человек,
 * работающий в портале, а не теми, которыми они называются внутри службы.
 *
 * «Инстанс» — слово из нашего кода: в нём и таблица `ch_instance`, и
 * маршруты API, и оно там останется. Но человек подключает НОМЕР: он его
 * так называет, за него платит и на него получает сообщения.
 * «Credentials» по той же причине становится ключами: это то, чем
 * открывают чужие службы.
 *
 * Названия обязаны различаться по существу, а не по порядку в списке.
 * «Ключи и доступы» и «Доступ» выходили в палитре подряд на запрос «дост»,
 * и понять, в каком из них заводят логин к чужой 1С, а в каком — роль
 * коллеге, по названию было нельзя. Разводит их направление: одни ключи мы
 * несём наружу, другой раздел решает, кого пускают к нам.
 *
 * Живут рядом с разбором адреса, а не в оболочке портала: название раздела
 * спрашивают и навигация, и заголовок экрана, и командная палитра, а общий
 * предок у них — только маршрут.
 */
export const SCREEN_TITLES: Record<ScreenName, string> = {
  instances: 'Номера',
  webhooks: 'Подписки',
  chats: 'Переписка',
  contacts: 'Контакты',
  pipelines: 'Сценарии',
  credentials: 'Ключи внешних служб',
  variables: 'Переменные',
  access: 'Доступ в портал',
}

// Держим список здесь одним местом: разбор адреса не должен знать компонент
// экрана, только его имя в пути.
const KNOWN_SCREENS: ReadonlySet<string> = new Set<ScreenName>([
  'instances',
  'chats',
  'webhooks',
  'contacts',
  'access',
  'pipelines',
  'credentials',
  'variables',
])

function isScreenName(value: string | undefined): value is ScreenName {
  return value !== undefined && KNOWN_SCREENS.has(value)
}

/**
 * Неизвестный или пустой адрес ведёт на экран инстансов: первое, зачем
 * заходят в админку, — подключить номер (FR-ADM-002).
 */
const DEFAULT_ROUTE: Route = { screen: 'instances' }

export function parseRoute(pathname: string): Route {
  const [prefix, screen, id] = pathname.split('/').filter((segment) => segment.length > 0)

  if (prefix !== 'app' || !isScreenName(screen)) return DEFAULT_ROUTE

  return id === undefined ? { screen } : { screen, id }
}

/**
 * Идентификатор карточки номера в списке номеров. Своего экрана у номера
 * нет: карточка живёт в общем списке, и попасть прямо к ней (из командной
 * палитры) можно только якорем — по нему браузер прокрутит сам.
 *
 * Живёт рядом с разбором адреса по той же причине, что и названия
 * разделов: это часть адреса, а не свойство экрана или палитры.
 */
export function якорьНомера(id: string): string {
  return `номер-${id}`
}
